import { Button } from "@aman/design-system";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { applyDocumentLocale } from "../i18n";

interface NavItem { to: string; label: string; icon: string; permission?: string }
interface NavGroup { label: string; items: NavItem[] }

const groups: NavGroup[] = [
  { label: "nav.main", items: [
    { to: "/", label: "nav.dashboard", icon: "⌂" },
    { to: "/products", label: "nav.products", icon: "◆", permission: "products.read" },
    { to: "/plans", label: "nav.plans", icon: "▤", permission: "plans.read" },
    { to: "/customers", label: "nav.customers", icon: "◎", permission: "customers.read" },
    { to: "/resellers", label: "nav.resellers", icon: "♧", permission: "resellers.read" },
  ] },
  { label: "nav.commerce", items: [
    { to: "/wallets", label: "nav.wallets", icon: "▱", permission: "wallets.read" },
    { to: "/vouchers", label: "nav.vouchers", icon: "▣", permission: "vouchers.read" },
    { to: "/orders", label: "nav.orders", icon: "◇", permission: "orders.read" },
    { to: "/payments", label: "nav.payments", icon: "◉", permission: "payments.read" },
  ] },
  { label: "nav.licensing", items: [
    { to: "/licenses", label: "nav.licenses", icon: "◇", permission: "licenses.read" },
    { to: "/devices", label: "nav.devices", icon: "▣", permission: "devices.read" },
    { to: "/appliances", label: "nav.appliances", icon: "A", permission: "devices.read" },
    { to: "/resources", label: "nav.resources", icon: "⌘", permission: "resources.read" },
    { to: "/credits", label: "nav.credits", icon: "✦", permission: "credits.read" },
    { to: "/operations", label: "nav.operations", icon: "↯", permission: "operations.read" },
    { to: "/operation-prices", label: "nav.operationPrices", icon: "≋", permission: "operation_prices.read" },
  ] },
  { label: "nav.platform", items: [
    { to: "/artifacts", label: "nav.artifacts", icon: "▧", permission: "artifacts.read" },
    { to: "/updates", label: "nav.updates", icon: "↑", permission: "updates.read" },
    { to: "/plugins", label: "nav.plugins", icon: "⬡", permission: "plugins.read" },
    { to: "/plugins/builder", label: "nav.pluginBuilder", icon: "✣", permission: "plugins.install" },
    { to: "/plugin-users", label: "nav.pluginUsers", icon: "♙", permission: "plugin_users.read" },
    { to: "/notifications", label: "nav.notifications", icon: "♢", permission: "notifications.read" },
  ] },
  { label: "nav.security", items: [
    { to: "/audit", label: "nav.audit", icon: "◷", permission: "audit.read" },
    { to: "/fraud", label: "nav.fraud", icon: "△", permission: "fraud.read" },
    { to: "/settings", label: "nav.settings", icon: "⚙", permission: "settings.read" },
    { to: "/backups", label: "nav.backups", icon: "▦", permission: "backups.read" },
  ] },
];

function Initials({ name }: { name: string }) {
  const text = name.trim().split(/\s+/u).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase();
  return <span className="avatar" aria-hidden="true">{text || "A"}</span>;
}

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("aman.theme") === "dark" ? "dark" : "light");
  useEffect(() => { setDrawerOpen(false); setProfileOpen(false); }, [location.pathname]);
  useEffect(() => { document.documentElement.dataset["theme"] = theme; localStorage.setItem("aman.theme", theme); }, [theme]);
  const visibleGroups = useMemo(() => groups.map((group) => ({ ...group, items: group.items.filter((item) => item.permission === undefined || auth.can(item.permission) || auth.user?.permissions.length === 0) })).filter((group) => group.items.length > 0), [auth]);
  const toggleLanguage = async (): Promise<void> => { const next = i18n.language === "ar" ? "en" : "ar"; await i18n.changeLanguage(next); applyDocumentLocale(next); };
  const logout = async (): Promise<void> => { await auth.logout(); navigate("/login", { replace: true }); };
  return (
    <div className="app-shell">
      <aside className={`sidebar ${drawerOpen ? "sidebar--open" : ""}`} aria-label={t("brand.name")}>
        <Link className="brand" to="/"><span className="brand__mark">أ</span><span><strong>{t("brand.name")}</strong><small>{t("brand.tagline")}</small></span></Link>
        <nav className="sidebar__nav">{visibleGroups.map((group) => <section className="nav-group" key={group.label}><h2>{t(group.label)}</h2>{group.items.map((item) => <NavLink end={item.to === "/"} className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`} to={item.to} key={item.to}><span className="nav-link__icon" aria-hidden="true">{item.icon}</span><span>{t(item.label)}</span></NavLink>)}</section>)}</nav>
        <div className="sidebar__footer"><span className="sidebar__status"><i /> Core API</span><span>v0.1.0</span></div>
      </aside>
      {drawerOpen ? <button className="sidebar-scrim" type="button" aria-label={t("common.close")} onClick={() => setDrawerOpen(false)} /> : null}
      <div className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" type="button" aria-label="Menu" onClick={() => setDrawerOpen(true)}>☰</button>
          <div className="topbar__context"><span className="environment-pill"><i /> Production</span></div>
          <div className="topbar__actions">
            <Button type="button" variant="ghost" size="small" onClick={() => void toggleLanguage()}>{t("common.language")}</Button>
            <button className="icon-button" type="button" title={t("common.theme")} onClick={() => setTheme((current) => current === "light" ? "dark" : "light")}>{theme === "light" ? "☾" : "☀"}</button>
            <div className="profile-menu">
              <button className="profile-trigger" type="button" aria-expanded={profileOpen} onClick={() => setProfileOpen((open) => !open)}><Initials name={auth.user?.displayName ?? "A"} /><span className="profile-trigger__text"><strong>{auth.user?.displayName}</strong><small>{auth.user?.roles[0] ?? auth.user?.type}</small></span><span aria-hidden="true">⌄</span></button>
              {profileOpen ? <div className="profile-popover"><Link to="/account">{t("common.account")}</Link><button type="button" onClick={() => void logout()}>{t("common.signOut")}</button></div> : null}
            </div>
          </div>
        </header>
        <main className="page-container"><Outlet /></main>
      </div>
    </div>
  );
}

export function ResellerLayout() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const toggleLanguage = async (): Promise<void> => { const next = i18n.language === "ar" ? "en" : "ar"; await i18n.changeLanguage(next); applyDocumentLocale(next); };
  const logout = async (): Promise<void> => { await auth.logout(); navigate("/login", { replace: true }); };
  return <div className="portal-shell"><header className="portal-header"><Link className="brand brand--compact" to="/reseller"><span className="brand__mark">أ</span><span><strong>{t("brand.name")}</strong><small>{t("nav.resellerPortal")}</small></span></Link><div className="portal-header__actions"><Button type="button" variant="ghost" size="small" onClick={() => void toggleLanguage()}>{t("common.language")}</Button><span>{auth.user?.displayName}</span><Button type="button" variant="secondary" size="small" onClick={() => void logout()}>{t("common.signOut")}</Button></div></header><main className="portal-content"><Outlet /></main></div>;
}

export function CustomerLayout() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const toggleLanguage = async (): Promise<void> => {
    const next = i18n.language === "ar" ? "en" : "ar";
    await i18n.changeLanguage(next);
    applyDocumentLocale(next);
  };
  const logout = async (): Promise<void> => { await auth.logout(); navigate("/login", { replace: true }); };
  return <div className="portal-shell"><header className="portal-header"><Link className="brand brand--compact" to="/customer"><span className="brand__mark">أ</span><span><strong>{t("brand.name")}</strong><small>{t("nav.customerPortal")}</small></span></Link><div className="portal-header__actions"><Button type="button" variant="ghost" size="small" onClick={() => void toggleLanguage()}>{t("common.language")}</Button><span>{auth.user?.displayName}</span><Button type="button" variant="secondary" size="small" onClick={() => void logout()}>{t("common.signOut")}</Button></div></header><main className="portal-content"><Outlet /></main></div>;
}
